import {usePromotion} from '@/context/Promotion/PromotionContext'
import {BuyType} from '@/utils/constants/promotionConstants'
import {useEffect, useMemo, useState} from 'react'

export const useSelectionModalContent = (type, initialValues = null) => {
  const {promotionConfig} = usePromotion()

  // Initialize with provided values or defaults
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [selectedTags, setSelectedTags] = useState(() => {
    if (initialValues?.itemType === BuyType.GTINS) {
      return {
        itemType: initialValues?.itemType,
        values: initialValues?.values?.map(value => ({...value, type: BuyType.GTINS}))
      }
    }

    return {
      itemType: initialValues?.itemType || '',
      values: initialValues?.values || [],
    }
  })

  const [combination, setCombination] = useState(initialValues?.productCombination || false)
  // Sync state when initialValues change (e.g., dropdown values updated before reopening modal)
  useEffect(() => {
    if (!initialValues) return

    // Map initial values into selectedTags structure
    if (initialValues?.itemType === BuyType.GTINS) {
      setSelectedTags({
        itemType: initialValues.itemType,
        values: (initialValues.values || []).map(value => ({...value, type: BuyType.GTINS})),
      })
    } else {
      setSelectedTags({
        itemType: initialValues.itemType || '',
        values: initialValues.values || [],
      })
    }

    setCombination(initialValues.productCombination || false)
  }, [initialValues])

  // Sync state when initialValues change (e.g., dropdown values updated before reopening modal)
  useEffect(() => {
    if (!initialValues) return

    // Map initial values into selectedTags structure
    if (initialValues?.itemType === BuyType.GTINS) {
      setSelectedTags({
        itemType: initialValues.itemType,
        values: (initialValues.values || []).map(value => ({...value, type: BuyType.GTINS})),
      })
    } else {
      setSelectedTags({
        itemType: initialValues.itemType || '',
        values: initialValues.values || [],
      })
    }

    setCombination(initialValues.productCombination || false)
  }, [initialValues])
  
  // Define tabs first (moved up to be available for useEffect)
  const tabs = useMemo(() => {
    const itemTypeOptions = promotionConfig?.conditions?.itemTypeOptions || []

    const selectedItemType = itemTypeOptions.find(item => {
      switch (type) {
        case BuyType.CATEGORY:
          return item.id === 'CATEGORY'
        case BuyType.BRAND:
          return item.id === 'BRAND'
        case BuyType.PRODUCT:
          return item.id === 'PRODUCTS'
        default:
          return false
      }
    })

    if (!selectedItemType || !selectedItemType.subTypes) {
      return []
    }

    // Use the subTypes as tabs
    return selectedItemType.subTypes || []
  }, [type, promotionConfig])

  // Set initial active tab index based on initialValues
  useEffect(() => {
    if (initialValues?.itemType && tabs.length > 0) {
      const tabIndex = tabs.findIndex(tab => tab.id === initialValues.itemType)
      if (tabIndex >= 0) {
        setActiveTabIndex(tabIndex)
      }
    }
  }, [tabs, initialValues?.itemType])

  const onTabChange = index => {
    setActiveTabIndex(index)
  }

  const getChildList = (tabs, type) => {
    // Get the active tab ID from the current tabs array
    const activeTabId = tabs?.[activeTabIndex]?.id

    if (!activeTabId) {
      return []
    }

    switch (type) {
      case BuyType.CATEGORY:
        switch (activeTabId) {
          case 'CATEGORY':
            return [
              {displayText: 'Snacks', id: '1'},
              {displayText: 'Beverages', id: '2'},
            ]
          case 'SUBCATEGORY':
            return [
              {displayText: 'Chips', id: '3'},
              {displayText: 'Juices', id: '4'},
            ]
          default:
            return []
        }

      default:
        return []
    }
  }

  // Compute whether primary button should be disabled
  const isPrimaryButtonDisabled = useMemo(() => {
    switch (type) {
      case BuyType.PRODUCT_ID:
      case BuyType.COMBO_ID:
      case BuyType.GTINS:
        // For PRODUCT_ID and GTINS, disable if no values are selected
        return !selectedTags.values || selectedTags.values.length === 0
      case BuyType.CATEGORY:
      case BuyType.BRAND:
      case BuyType.PRODUCT:
        // For other types, also check if any values are selected
        return !selectedTags.values || selectedTags.values.length === 0
      default:
        return false
    }
  }, [type, selectedTags.values])

  return {
    activeTabIndex,
    selectedTags,
    combination,
    tabs,
    onTabChange,
    getChildList,
    setSelectedTags,
    setCombination,
    isPrimaryButtonDisabled,
  }
}
